import { useHttp } from "../http/useHttp";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { useMemo } from "react";
import { useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

export type PaymentlikeStatus = "Pending" | "Paid" | "Failed";
type ServiceChargeInvoicesOperation =
  paths["/dashboard/users/{api_token_uuid}/service-charges/invoices"]["post"];
type UseServiceChargeInvoicesResponse =
  ServiceChargeInvoicesOperation["responses"][200]["content"]["application/json"];
type UseServiceChargeInvoicesRequestBody =
  ServiceChargeInvoicesOperation["requestBody"]["content"]["application/json"];
type ServiceChargeInvoicesPath = ServiceChargeInvoicesOperation["parameters"]["path"];

export type IServiceChargeInvoice = UseServiceChargeInvoicesResponse["items"][number];
export type UseServiceChargeInvoicesInput = UseServiceChargeInvoicesRequestBody & {
  user_uuid: ServiceChargeInvoicesPath["api_token_uuid"];
};

export const useServiceChargeInvoices = (
  input: UseServiceChargeInvoicesInput = { user_uuid: "" },
  options: Omit<
    UseAxiosQueryOptions<UseServiceChargeInvoicesResponse>,
    "queryKey" | "queryFn"
  > = {},
) => {
  const { client } = useHttp();

  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<UseServiceChargeInvoicesResponse>({
      queryKey: ["user-service-charge-invoices", input],
      queryFn: ({ signal }) => {
        return client.post(`/users/${input.user_uuid}/service-charges/invoices`, input, { signal });
      },
      enabled: options.enabled !== false && !!input.user_uuid,
      ...options,
    });

  const [invoices, hasNextPage] = useMemo(() => {
    if (!data) {
      return [[], false];
    }
    return [data.data.items, data.data.has_next_page];
  }, [data]);

  useDefaultError(error);

  return {
    invoices,
    hasNextPage,
    error,
    isLoading,
    isFetching,
    isPending,
    status,
    refetch,
  };
};

export type UseServiceChargeInvoicesReturn = ReturnType<typeof useServiceChargeInvoices>;
